import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import styles from './Venue.module.css';
import shared from './Shared.module.css';
import { EventModel, VenueSummary } from '../models/EventModel';
import EventItem from '../components/EventItem';
import SubscriptionSection from '../components/SubscriptionSection';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { buildVenuePageSeo } from '../utils/eventSeo';

const defaultTimezone = 'America/Toronto';
const VenueMiniMap = lazy(() => import('../components/VenueMiniMap'));

function getTodayInTimezone(timezone: string): string {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(new Date());
    const year = parts.find((part) => part.type === 'year')?.value ?? '';
    const month = parts.find((part) => part.type === 'month')?.value ?? '';
    const day = parts.find((part) => part.type === 'day')?.value ?? '';

    return `${year}-${month}-${day}`;
}

export default function Venue() {
    const preloadedEvents = (window as any).__VENUE_EVENTS__ ?? [];
    const preloadedVenue = (window as any).__VENUE__ ?? null;
    const [events, setEvents] = useState<EventModel[]>(preloadedEvents || []);
    const [venue, setVenue] = useState<VenueSummary | null>(preloadedVenue || null);
    const [loading, setLoading] = useState<boolean>(!preloadedVenue);
    const [isMapOpen, setIsMapOpen] = useState(false);

    const { slug } = useParams();
    const venueSlug = decodeURIComponent(slug || "");

    useEffect(() => {
        setEvents(preloadedEvents || []);
        setVenue(preloadedVenue || null);
    }, [preloadedEvents, preloadedVenue]);

    useEffect(() => {
        setIsMapOpen(false);
    }, [venueSlug]);

    useEffect(() => {
        let cancelled = false;

        async function fetchVenuePage() {
            if (!venueSlug) {
                setVenue(null);
                setEvents([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const venueResponse = await fetch(`/api/events/venues/${encodeURIComponent(venueSlug)}`);
                if (!venueResponse.ok) {
                    throw new Error('Lieu introuvable.');
                }

                const venueData = await venueResponse.json() as VenueSummary;
                const startDate = getTodayInTimezone(defaultTimezone);
                const eventParams = new URLSearchParams({
                    venue: venueSlug,
                    timezone: defaultTimezone,
                    date: startDate,
                    upcoming: '1',
                    limit: '50'
                });
                const eventsResponse = await fetch(`/api/events?${eventParams.toString()}`);
                if (!eventsResponse.ok) {
                    throw new Error("Impossible de charger les événements du lieu.");
                }

                const eventData = await eventsResponse.json() as EventModel[];

                if (!cancelled) {
                    setVenue(venueData);
                    setEvents(Array.isArray(eventData) ? eventData.slice(0, 50) : []);
                }
            } catch (error) {
                console.error('Failed to load venue page:', error);
                if (!cancelled) {
                    setVenue(null);
                    setEvents([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        const preloadedMatches = preloadedVenue?.slug === venueSlug;
        if (!preloadedMatches) {
            void fetchVenuePage();
            return () => {
                cancelled = true;
            };
        }

        setLoading(false);
        return () => {
            cancelled = true;
        };
    }, [venueSlug, preloadedVenue]);

    const venueName = venue?.name || venueSlug;
    const hasLatitudeValue = venue?.latitude !== null && venue?.latitude !== undefined && String(venue.latitude).trim() !== "";
    const hasLongitudeValue = venue?.longitude !== null && venue?.longitude !== undefined && String(venue.longitude).trim() !== "";
    const latitude = hasLatitudeValue ? Number(venue.latitude) : NaN;
    const longitude = hasLongitudeValue ? Number(venue.longitude) : NaN;
    const hasMapCoordinates = hasLatitudeValue && hasLongitudeValue && Number.isFinite(latitude) && Number.isFinite(longitude);
    const seo = useMemo(
        () => buildVenuePageSeo(venue, { baseUrl: location.origin, slug: venue?.slug ?? venueSlug }),
        [venue, venueSlug]
    );

    return <div className={styles.pageShell} style={{position: 'relative'}}>
        <Helmet>
            <title>{seo.title}</title>
            <meta name="description" content={seo.metaDescription} />
            <meta name="keywords" content={seo.keywords} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.metaDescription} />
            <meta property="og:url" content={seo.canonicalUrl} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.metaDescription} />
            <link rel="canonical" href={seo.canonicalUrl} />
        </Helmet>

        {venue && (
            <section className={`${styles.venueMeta} ${!venue.imageUrl ? styles.venueMetaNoImage : ''}`}>
                {venue.imageUrl && (
                    <img src={venue.imageUrl} alt={`Photo du lieu ${venue.name}`} className={styles.venueImage} />
                )}
                <div className={styles.venueDetails}>
                    {venue.venueType && <span className={styles.typePill}>{venue.venueType}</span>}
                    <h1 className={styles.venueHeading}>{venueName}</h1>
                    {venue.address && <p className={styles.addressLine}><strong>Adresse:</strong> {venue.address}</p>}
                    {venue.description && <p className={styles.venueDescription}>{venue.description}</p>}
                    <div className={styles.actionsRow}>
                        {venue.websiteUrl && (
                            <a
                                href={venue.websiteUrl}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.smallAction}
                                aria-label={`Visiter le site web de ${venue.name} dans un nouvel onglet`}
                            >
                                Site web <span aria-hidden="true">↗</span>
                            </a>
                        )}
                        {hasMapCoordinates && (
                            <button
                                type="button"
                                className={styles.smallAction}
                                onClick={() => setIsMapOpen((current) => !current)}
                                aria-expanded={isMapOpen}
                                aria-controls="venue-map-panel"
                            >
                                {isMapOpen ? 'Masquer la carte' : 'Voir sur la carte'}
                            </button>
                        )}
                    </div>

                    {hasMapCoordinates && isMapOpen && (
                        <div id="venue-map-panel" className={styles.mapSection}>
                            <div className={styles.mapFrame}>
                                <Suspense fallback={<div className={styles.mapFallback}>Chargement de la carte...</div>}>
                                    <VenueMiniMap
                                        latitude={latitude}
                                        longitude={longitude}
                                        title={venueName}
                                    />
                                </Suspense>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        )}

        {loading && <p>Chargement...</p>}

        {!loading && <h2 className={styles.eventsTitle}>À venir</h2>}

        <div className={styles.eventsContainer}>
            {!loading && events.length == 0 && <div className={styles.emptyState}>
                <p>Il n'y a aucun événement ou activité à venir pour l'instant. </p>
                </div>}
            {events.map(e =>
                <EventItem key={`${e.id}-${e.dates[0]?.date ?? 'venue'}`} event={e} />
            )}
        </div>

        <div className={styles.bottomCtas}>
            <Link to="/ville-quebec/aujourdhui" reloadDocument className={`${shared.ctaBtn} ${styles.bottomCtaButton}`}>Voir quoi faire à Québec aujourd'hui</Link>
            <Link to="/ville-quebec/evenements-cette-semaine" reloadDocument className={`${shared.ctaBtn} ${styles.bottomCtaButton}`}>Voir tous les événements de cette semaine</Link>
            <Link to="/#search" className={`${shared.ctaBtn} ${styles.bottomCtaButton}`}>Rechercher parmi tous les événements</Link>
        </div>
        <SubscriptionSection />
    </div>;
}
